/**
 * Project Data
 *
 * Merges project data from multiple sources:
 * 1. fluffleProjects.json - from Fluffle API (fetched from data repo)
 * 2. customProjects.json - local additions/overrides (fetched from data repo)
 * 3. OLI labels - dynamically loaded from backend WebSocket
 */

// Bundled fallback data
import fluffleProjectsFallback from './fluffleProjects.json';
import customProjectsFallback from './customProjects.json';

// Public data repo - no auth needed
const DATA_URL = 'https://raw.githubusercontent.com/tomx-eth/megaarmy-data/main/data';

// Default MegaETH logo for unknown contracts
export const DEFAULT_LOGO = 'https://logo.svgcdn.com/token-branded/mega-eth.png';

// Default project for unknown contracts (used when we have OLI label but no project metadata)
export const DEFAULT_PROJECT: Project = {
  name: 'Unknown',
  img: DEFAULT_LOGO,
  description: 'Contract on MegaETH',
  category: 'Unknown',
};

// OLI labels loaded dynamically from backend
let oliLabels: Record<string, string> = {};
let oliLabelsLoaded = false;

// Full OLI label data (includes contractName, category, etc.)
export interface OliLabel {
  address: string;
  projectName?: string;
  contractName?: string;
  category?: string;
  source?: string;
}
let oliLabelsFull: Map<string, OliLabel> = new Map();

export interface Project {
  img: string;
  name?: string;
  address?: string;
  twitter?: string;
  description?: string;
  category?: string;
  website?: string;
  megaMafia?: boolean;
  [key: string]: any;
}

// OLI name -> Fluffle/custom name mapping (for projects with different naming conventions)
const OLI_TO_FLUFFLE_NAME: Record<string, string> = {
  'redstone-finance': 'redstone',
  'mega-warren': 'mega_warren',
  'prismfi': 'prism',
  'warpx': 'warpx',
  'magpieprotocol': 'fly trade',
  'currentx-dex': 'currentx',
  'worldlibertyfinancial': 'world-liberty-financial',
  'beefy-finance': 'beefy finance',
  'noxa-fun': 'noxa fun',
  'orbiter-finance': 'orbiter finance',
  'world-markets': 'world markets',
  'kumbaya-xyz': 'kumbaya', // OLI uses kumbaya-xyz, Fluffle uses Kumbaya
};

/**
 * Normalize a project name for consistent circle key generation
 */
function normalizeProjectName(name: string): string {
  return name.toLowerCase().replace(/[-_\s]+/g, '');
}

/**
 * Format a slug name to a display name (e.g., "kumbaya-xyz" -> "Kumbaya")
 */
function formatSlugToDisplayName(slug: string): string {
  if (!slug) return 'Unknown';
  let name = slug
    .replace(/[-_](xyz|io|app|fi|finance|protocol|labs|network|exchange|dex|swap)$/i, '')
    .replace(/[-_]/g, ' ');
  return name.split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Project data maps
const projectsByName = new Map<string, Project>();
const staticAddressToProjectName: Record<string, string> = {};
const projectToAddresses: Record<string, string[]> = {};

// Load projects from arrays
function loadProjects(fluffleData: Project[], customData: Project[]): void {
  projectsByName.clear();

  // Add fluffle projects first
  for (const project of fluffleData) {
    if (project.name) {
      const lowerName = project.name.toLowerCase();
      projectsByName.set(lowerName, project);
      projectsByName.set(normalizeProjectName(lowerName), project);
      if (project.address) {
        staticAddressToProjectName[project.address.toLowerCase()] = project.name;
      }
    }
  }

  // Add custom projects (override fluffle)
  for (const project of customData) {
    if (project.name) {
      const lowerName = project.name.toLowerCase();
      projectsByName.set(lowerName, project);
      projectsByName.set(normalizeProjectName(lowerName), project);
      if (project.address) {
        staticAddressToProjectName[project.address.toLowerCase()] = project.name;
      }
    }
  }

  // Add OLI name aliases
  for (const [oliName, fluffleName] of Object.entries(OLI_TO_FLUFFLE_NAME)) {
    const project = projectsByName.get(fluffleName) || projectsByName.get(normalizeProjectName(fluffleName));
    if (project) {
      projectsByName.set(oliName, project);
      projectsByName.set(normalizeProjectName(oliName), project);
    }
  }

  // Build reverse mapping
  for (const [address, projectName] of Object.entries(staticAddressToProjectName)) {
    const normalizedName = projectName.toLowerCase();
    if (!projectToAddresses[normalizedName]) {
      projectToAddresses[normalizedName] = [];
    }
    projectToAddresses[normalizedName].push(address.toLowerCase());
  }

  // Update exports
  PROJECTS.length = 0;
  PROJECTS.push(...new Set(projectsByName.values()));
}

// Mutable exports
export const PROJECTS: Project[] = [];
export const ADDRESS_TO_PROJECT: Record<string, string> = staticAddressToProjectName;
export const PROJECT_TO_ADDRESSES: Record<string, string[]> = projectToAddresses;

// Initialize with bundled data immediately
loadProjects(fluffleProjectsFallback as Project[], customProjectsFallback as Project[]);

// Then fetch fresh data from public repo
fetch(`${DATA_URL}/customProjects.json`)
  .then(r => r.ok ? r.json() : Promise.reject())
  .then(customData => {
    fetch(`${DATA_URL}/fluffleProjects.json`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(fluffleData => {
        loadProjects(fluffleData, customData);
        console.log(`[Projects] Loaded ${PROJECTS.length} projects from data repo`);
      })
      .catch(() => console.log('[Projects] Using bundled fluffle data'));
  })
  .catch(() => console.log('[Projects] Using bundled data'));

/**
 * Update OLI labels from backend
 */
export function updateOliLabels(labels: Record<string, string>): void {
  const normalizedLabels: Record<string, string> = {};
  for (const [address, projectName] of Object.entries(labels)) {
    normalizedLabels[address.toLowerCase()] = projectName;
  }
  oliLabels = normalizedLabels;
  oliLabelsLoaded = true;
}

export function areOliLabelsLoaded(): boolean {
  return oliLabelsLoaded;
}

export function updateOliLabelsFull(labels: OliLabel[]): void {
  oliLabelsFull.clear();
  for (const label of labels) {
    oliLabelsFull.set(label.address.toLowerCase(), label);
  }
}

export function getOliLabelForAddress(address: string): OliLabel | undefined {
  return oliLabelsFull.get(address.toLowerCase());
}

export function getProjectNameForAddress(address: string): string | undefined {
  const normalizedAddress = address.toLowerCase();
  if (oliLabels[normalizedAddress]) {
    return oliLabels[normalizedAddress];
  }
  return staticAddressToProjectName[normalizedAddress];
}

export function getAddressesForProject(projectName: string): string[] {
  return PROJECT_TO_ADDRESSES[projectName.toLowerCase()] || [];
}

export function getProjectByName(name: string): Project | undefined {
  const lowerName = name.toLowerCase();
  const exact = projectsByName.get(lowerName);
  if (exact) return exact;
  const mapped = OLI_TO_FLUFFLE_NAME[lowerName];
  if (mapped) {
    const mappedProject = projectsByName.get(mapped);
    if (mappedProject) return mappedProject;
  }
  return projectsByName.get(normalizeProjectName(lowerName));
}

/**
 * Format OLI category for display
 */
function formatCategory(category: string): string {
  const map: Record<string, string> = {
    'dex': 'DEX',
    'gambling': 'Gaming',
    'defi': 'DeFi',
    'derivative': 'DeFi',
    'non_fungible_tokens': 'NFT',
    'nft_marketplace': 'NFT',
    'fungible_tokens': 'Token',
    'bridge': 'Bridge',
    'oracle': 'Oracle',
    'social': 'Social',
    'gaming': 'Gaming',
    'infrastructure': 'Infra',
    'wallet': 'Wallet',
    'exchange': 'Exchange',
  };
  return map[category.toLowerCase()] || category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, ' ');
}

export function getProjectByAddress(address: string): Project | undefined {
  const projectName = getProjectNameForAddress(address);
  if (!projectName) return undefined;

  const project = getProjectByName(projectName);
  if (project) return project;

  // Create default project with formatted name
  const oliLabel = getOliLabelForAddress(address);
  return {
    name: formatSlugToDisplayName(projectName),
    img: DEFAULT_LOGO,
    description: oliLabel?.contractName || 'Contract on MegaETH',
    category: oliLabel?.category ? formatCategory(oliLabel.category) : 'Unknown',
  };
}

export function getCircleKeyForAddress(address: string): string {
  const normalizedAddress = address.toLowerCase();

  if (normalizedAddress.startsWith('project:')) {
    const projectNamePart = normalizedAddress.substring('project:'.length);
    const mappedName = OLI_TO_FLUFFLE_NAME[projectNamePart] || projectNamePart;
    return `project:${normalizeProjectName(mappedName)}`;
  }

  const projectName = getProjectNameForAddress(normalizedAddress);
  if (projectName) {
    const lowerName = projectName.toLowerCase();
    const mappedName = OLI_TO_FLUFFLE_NAME[lowerName] || lowerName;
    return `project:${normalizeProjectName(mappedName)}`;
  }
  return normalizedAddress;
}

export function isProjectKey(key: string): boolean {
  return key.startsWith('project:');
}

export function getProjectNameFromKey(key: string): string | null {
  if (!key.startsWith('project:')) return null;
  return key.substring('project:'.length);
}

export function getAddressesForCircleKey(key: string): string[] {
  if (key.startsWith('project:')) {
    const normalizedKey = key.substring('project:'.length);
    const addressSet = new Set<string>();

    for (const [projectName, addresses] of Object.entries(PROJECT_TO_ADDRESSES)) {
      if (normalizeProjectName(projectName) === normalizedKey) {
        for (const addr of addresses) {
          addressSet.add(addr);
        }
      }
    }

    for (const [address, oliProjectName] of Object.entries(oliLabels)) {
      const mappedName = OLI_TO_FLUFFLE_NAME[oliProjectName.toLowerCase()] || oliProjectName.toLowerCase();
      if (normalizeProjectName(mappedName) === normalizedKey) {
        addressSet.add(address.toLowerCase());
      }
    }

    return Array.from(addressSet);
  }
  return [key];
}

export function getPrimaryAddressForCircleKey(key: string): string {
  if (key.startsWith('project:')) {
    const addresses = getAddressesForCircleKey(key);
    return addresses.sort()[0] || key;
  }
  return key;
}

export function isMegaMafia(projectName: string): boolean {
  const project = getProjectByName(projectName);
  return project?.megaMafia === true;
}

export const CATEGORIES = [
  'All',
  'DeFi',
  'DEX',
  'Gaming',
  'NFT',
  'Bridge',
  'Token',
  'Infra',
  'Social',
  'Oracle',
  'Wallet',
] as const;

export type Category = typeof CATEGORIES[number];

export function getProjectCategories(): string[] {
  const categories = new Set<string>();
  for (const project of PROJECTS) {
    if (project.category) {
      categories.add(project.category);
    }
  }
  return Array.from(categories).sort();
}

// Legacy exports
export type FluffleProject = Project;
export const FLUFFLE_PROJECTS = PROJECTS;
export const ADDRESS_TO_DAPP = ADDRESS_TO_PROJECT;
